<template>
  <div class="lp-tree">
    <div v-if="showToggle" class="xmy-2">
      <lp-button
        v-if="!isExpanded"
        class="lp-tree-expand-all"
        data-ua-unique-id="ua-tree-expand-all"
        :size="Sizes.SMALL"
        text
        :label="t('LpTree.ExpandAll')"
        @click="expandAll"
      />
      <lp-button
        v-if="isExpanded"
        class="lp-tree-collapse-all"
        data-ua-unique-id="ua-tree-collapse-all"
        :size="Sizes.SMALL"
        text
        :label="t('LpTree.CollapseAll')"
        @click="collapseAll"
      />
    </div>
    <div v-if="showSearch" class="mb-1">
      <lp-input
        v-model="searchValue.value"
        :placeholder="t('Shared.FilterOptions')"
        prefix-icon="search"
        size="compact"
        @keyup="onSearchEmit"
      >
        <template #clear>
          <lp-button
            v-if="searchValue.value"
            icon="close"
            text
            @click="clearInput"
          />
        </template>
      </lp-input>
    </div>
    <div v-if="selectedNode" class="ly-row ly-spacebetween-center xmb-3">
      <span
        class="text-indigo-100"
        data-ua-unique-id="ua-tree-selected-node"
        v-html="formatTraversalLabel(selectedNode)"
      />
      <lp-button
        icon="times"
        text
        :size="Sizes.XSMALL"
        @click="selectedNode = null"
      />
    </div>
    <div
      v-show="results === null && !isLoadingSearchResults"
      role="tree"
      class="bg-black-10 xpx-2 xpy-4 rounded-lg"
    >
      <lp-tree-node
        :children="tree.root[childrenBy] ?? null"
        :read-only="readOnly"
        @node-select="handleNodeSelect"
      />
    </div>
    <lp-spinner v-if="isLoadingSearchResults" />
    <div v-show="results && !isLoadingSearchResults" role="tree">
      <div
        v-for="(result, index) in results"
        :key="index"
        class="mt-1"
        role="treeitem"
        :aria-selected="result.checked"
      >
        <span
          v-if="readOnly"
          class="xml-5 cursor-pointer font-weight-bold"
          @click="handleNodeSelect(result)"
        >
          {{ traversalLabel(result) }}
        </span>
        <lp-checkbox
          v-else
          :id="result.key"
          :key="result.key"
          v-model="result.checked"
          :label="traversalLabel(result)"
          :disabled="result.disabled"
          @update:model-value="
            result[childrenBy].length && toggleChildren(result, result.checked)
          "
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'lp-tree',
  inheritAttrs: false,
}
</script>

<script setup lang="ts">
import LpTreeNode from './LpTreeNode.vue'
import { withDefaults } from 'vue'
import { TreeNode } from './tree'
import { Sizes } from '@/models/enums'
import { useTree } from './composables/use-tree'
import { t } from '@/plugins/i18n'
import LpButton from '@/components/button'

interface Props {
  modelValue?: TreeNode[] | null
  children: Record<string, unknown> | Record<string, unknown>[] | null
  disabled?: boolean
  trackBy?: string
  labelBy?: string
  childrenBy?: string
  showToggle?: boolean
  showSearch?: boolean
  readOnly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  children: null,
  disabled: false,
  trackBy: 'value',
  labelBy: 'name',
  childrenBy: 'children',
  showToggle: false,
  showSearch: false,
  readOnly: false,
})

const emit = defineEmits(['update:modelValue', 'node-select'])

const {
  tree,
  isExpanded,
  results,
  isLoadingSearchResults,
  searchValue,
  selectedNode,
  traversalLabel,
  clearInput,
  onSearchEmit,
  expandAll,
  collapseAll,
  toggleChildren,
  handleNodeSelect,
  formatTraversalLabel,
} = useTree(props, emit)
</script>
